import { motion } from 'motion/react';

type ProgramsSpotlightProps = {
	eyebrow?: string;
	title?: string;
	description?: string;
	itemOneTitle?: string;
	itemOneDescription?: string;
	itemOneImageUrl?: string;
	itemOneImageAlt?: string;
	itemTwoTitle?: string;
	itemTwoDescription?: string;
	itemTwoImageUrl?: string;
	itemTwoImageAlt?: string;
	itemThreeTitle?: string;
	itemThreeDescription?: string;
	itemThreeImageUrl?: string;
	itemThreeImageAlt?: string;
};

export function ProgramsSpotlight({
	eyebrow,
	title,
	description,
	itemOneTitle,
	itemOneDescription,
	itemOneImageUrl,
	itemOneImageAlt,
	itemTwoTitle,
	itemTwoDescription,
	itemTwoImageUrl,
	itemTwoImageAlt,
	itemThreeTitle,
	itemThreeDescription,
	itemThreeImageUrl,
	itemThreeImageAlt,
}: ProgramsSpotlightProps) {
	const items = [
		{
			title:    itemOneTitle        || 'Daily Prayer',
			desc:     itemOneDescription  || 'Nitnem, divans, and prayer timings — clear and always accessible.',
			imageUrl: itemOneImageUrl     || '',
			imageAlt: itemOneImageAlt    || 'Daily Prayer',
		},
		{
			title:    itemTwoTitle        || 'Learning & Youth',
			desc:     itemTwoDescription  || 'Sikh studies, Punjabi classes, and youth-centered activities.',
			imageUrl: itemTwoImageUrl     || '',
			imageAlt: itemTwoImageAlt    || 'Learning and Youth',
		},
		{
			title:    itemThreeTitle      || 'Seva & Community',
			desc:     itemThreeDescription || 'Langar, volunteer efforts, and local service — open to all.',
			imageUrl: itemThreeImageUrl   || '',
			imageAlt: itemThreeImageAlt  || 'Seva and Community',
		},
	];

	return (
		<section className="relative overflow-hidden border-b border-border bg-background">
			{/* Subtle warm glow anchored top-left */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute left-0 top-0 h-[480px] w-[480px] -translate-x-1/3 -translate-y-1/3 rounded-full bg-accent/[0.045] blur-[120px]"
			/>

			{/* ── Section header ─────────────────────────────────── */}
			<div className="relative mx-auto max-w-6xl px-6 pb-0 pt-20 md:px-12 lg:px-16">
				<motion.div
					initial={{ opacity: 0, y: 18 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.06 }}
					transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
					className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between"
				>
					<div>
						<div className="flex items-center gap-3">
							<div className="h-px w-6 shrink-0 bg-accent/60" aria-hidden="true" />
							<p className="text-[10px] font-semibold uppercase tracking-[0.44em] text-accent/75">
								{eyebrow || 'Core offerings'}
							</p>
						</div>
						<h2 className="mt-5 font-serif text-4xl leading-[1.02] tracking-tight text-foreground md:text-5xl">
							{title || 'Main areas of temple life.'}
						</h2>
					</div>
					<p className="max-w-xs text-[13.5px] leading-[1.85] text-foreground/42 lg:text-right">
						{description || 'Three pillars that shape daily life at the Gurdwara — structured, clean, and easy to expand.'}
					</p>
				</motion.div>
			</div>

			{/* ── Three-column grid ──────────────────────────────── */}
			<div className="relative mx-auto mt-14 max-w-6xl border-t border-border/70">
				<div className="grid md:grid-cols-3">
					{items.map((item, i) => (
						<motion.div
							key={item.title}
							initial={{ opacity: 0, y: 28 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.06 }}
							transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
							className={[
								'group relative flex flex-col border-border/70',
								'border-b md:border-b-0',
								i < items.length - 1 ? 'md:border-r' : '',
							].join(' ')}
						>
							{/* Image — top of card, full-bleed */}
							<div className="relative aspect-[16/10] w-full overflow-hidden bg-border/40">
								{item.imageUrl ? (
									<>
										<img
											src={item.imageUrl}
											alt={item.imageAlt}
											className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
										/>
										{/* Bottom dissolve into card body */}
										<div
											aria-hidden="true"
											className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background/30 to-transparent"
										/>
									</>
								) : (
									<div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
										{/* Refined architectural placeholder */}
										<div className="relative h-16 w-10 opacity-25">
											<div className="absolute inset-x-1/2 bottom-0 h-full w-px -translate-x-1/2 bg-foreground/60" />
											<div className="absolute left-1/2 top-0 h-6 w-7 -translate-x-1/2 border border-foreground/60 bg-foreground/[0.06]" />
											<div className="absolute bottom-0 left-0 right-0 h-px bg-foreground/40" />
											<div className="absolute left-1/2 top-[44%] h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-accent/90 shadow-[0_0_6px_rgba(184,154,50,0.7)]" />
										</div>
										<span className="text-[9px] font-semibold uppercase tracking-[0.44em] text-foreground/22">
											Add image
										</span>
									</div>
								)}

								{/* Gold top accent line */}
								<div
									aria-hidden="true"
									className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
								/>
							</div>

							{/* Card body */}
							<div className="flex flex-1 flex-col px-7 py-8">
								{/* Index */}
								<p className="font-serif text-[11px] italic tracking-[0.02em] text-foreground/22">
									0{i + 1}
								</p>

								{/* Title */}
								<p className="mt-3 font-serif text-[1.4rem] leading-[1.15] tracking-tight text-foreground transition-colors duration-200 group-hover:text-accent">
									{item.title}
								</p>

								{/* Rule */}
								<div
									aria-hidden="true"
									className="mt-5 h-px w-8 bg-foreground/15 transition-all duration-500 group-hover:w-12 group-hover:bg-accent/60"
								/>

								{/* Description */}
								<p className="mt-4 text-[13.5px] leading-[1.85] text-foreground/42">
									{item.desc}
								</p>
							</div>
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
}
